import { useSignal } from "@preact/signals";
import { IcoImage, IcoUser } from "../icons";

type ImagePickerProps = {
  /** Current image URL (if any) */
  value?: string | null;
  /** Callback when image is selected */
  onChange?: (file: File | null) => void;
  /** Size of the picker */
  size?: "md" | "lg";
  /** Placeholder text shown below the picker */
  label?: string;
};

/**
 * Profile picture picker component.
 * Shows a circular preview with upload capability.
 *
 * TODO: Integrate with S3 for actual image uploads.
 * For now, this just handles the UI and file selection.
 */
export function ImagePicker({
  value,
  onChange,
  size = "lg",
  label = "Add photo",
}: ImagePickerProps) {
  const previewUrl = useSignal<string | null>(value ?? null);
  const isDragging = useSignal(false);

  const sizeClass = size === "lg" ? "size-24" : "size-16";
  const iconSize = size === "lg" ? "size-10" : "size-6";

  function handleFileSelect(file: File | null) {
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        return;
      }
      // Create preview URL
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
      }
      previewUrl.value = URL.createObjectURL(file);
    } else {
      if (previewUrl.value && !value) {
        URL.revokeObjectURL(previewUrl.value);
      }
      previewUrl.value = null;
    }
    onChange?.(file);
  }

  function handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    handleFileSelect(file);
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDragging.value = false;
    const file = e.dataTransfer?.files?.[0] ?? null;
    handleFileSelect(file);
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    isDragging.value = true;
  }

  function handleDragLeave() {
    isDragging.value = false;
  }

  return (
    <div class="flex flex-col items-center gap-2">
      <label
        class={`
          relative cursor-pointer group
          ${sizeClass} rounded-full
          bg-surface-muted border-2 border-dashed
          ${isDragging.value ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20" : "border-border hover:border-text-muted"}
          flex items-center justify-center
          overflow-hidden transition-colors
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {previewUrl.value ? (
          <>
            <img
              src={previewUrl.value}
              alt="Profile preview"
              class="w-full h-full object-cover"
            />
            <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <IcoImage class="size-6 text-white" />
            </div>
          </>
        ) : (
          <div class="flex flex-col items-center justify-center text-text-muted group-hover:text-text transition-colors">
            <IcoUser class={iconSize} />
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          class="sr-only"
          onChange={handleInputChange}
        />
      </label>
      <span class="text-sm text-text-muted">{label}</span>
    </div>
  );
}
